import type { CredentialType } from '@adminhub/graphql-types';

import type { FeatureEvents, ScreenEvent, TrackEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject, TrackAction, TrackActionSubject } from '../new-analytics-types';

type ScreenKeys = 'serviceAccountListScreen' | 'serviceAccountDetailsScreen';
type UIEventKeys =
  | 'createNewServiceAccount'
  | 'confirmCreateServiceAccount'
  | 'showServiceAccountDetails'
  | 'deleteServiceAccount'
  | 'confirmDeleteServiceAccount'
  | 'createServiceAccountCredentails'
  | 'confirmCreateServiceAccountCredentails'
  | 'revokeServiceAccountCredential'
  | 'confirmRevokeServiceAccountCredential'
  | 'editServiceAccontDetails'
  | 'confirmEditServiceAccontDetails';

type TrackEventKeys =
  | 'serviceAccountCreated'
  | 'serviceAccountDeleted'
  | 'serviceAccountCredentialCreated'
  | 'serviceAccountCredentialRevoked';

export enum ServiceAccountsScreens {
  serviceAccountListScreen = 'serviceAccountListScreen',
  serviceAccountDetailsScreen = 'serviceAccountDetailsScreen',
}

export const serviceAccountsEvents: FeatureEvents<ScreenKeys, UIEventKeys, TrackEventKeys, string> = {
  confluenceSpec: 'https://hello.atlassian.net/wiki/spaces/CloudSecurity/pages/5634934245/Service+Account+FE+Analytics',
  screen: {
    serviceAccountListScreen: (attributes: { orgId: string }): ScreenEvent => ({
      data: {
        name: ServiceAccountsScreens.serviceAccountListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86970',
    }),
    serviceAccountDetailsScreen: (attributes: { orgId: string; serviceAccountId: string }): ScreenEvent => ({
      data: {
        name: ServiceAccountsScreens.serviceAccountDetailsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86971',
    }),
  },
  ui: {
    createNewServiceAccount: (attributes: { orgId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'createNewServiceAccount',
        source: ServiceAccountsScreens.serviceAccountListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86941',
    }),
    confirmCreateServiceAccount: (attributes: { orgId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'confirmCreateServiceAccount',
        source: ServiceAccountsScreens.serviceAccountListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86942',
    }),
    showServiceAccountDetails: (attributes: { orgId: string; serviceAccountId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'showServiceAccountDetails',
        source: ServiceAccountsScreens.serviceAccountListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86943',
    }),
    deleteServiceAccount: (attributes: { orgId: string; serviceAccountId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'deleteServiceAccount',
        source: ServiceAccountsScreens.serviceAccountListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86944',
    }),
    confirmDeleteServiceAccount: (attributes: { orgId: string; serviceAccountId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'confirmDeleteServiceAccount',
        source: ServiceAccountsScreens.serviceAccountListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86945',
    }),
    createServiceAccountCredentails: (attributes: {
      orgId: string;
      serviceAccountId: string;
      source: keyof typeof ServiceAccountsScreens;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'createServiceAccountCredentails',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86947',
    }),
    confirmCreateServiceAccountCredentails: (attributes: {
      orgId: string;
      serviceAccountId: string;
      credentialType: CredentialType;
      source: keyof typeof ServiceAccountsScreens;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'confirmCreateServiceAccountCredentails',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86949',
    }),
    revokeServiceAccountCredential: (attributes: {
      orgId: string;
      serviceAccountId: string;
      credentialId: string | string[];
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'revokeServiceAccountCredential',
        source: ServiceAccountsScreens.serviceAccountDetailsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86951',
    }),
    confirmRevokeServiceAccountCredential: (attributes: {
      orgId: string;
      serviceAccountId: string;
      credentialId: string | string[];
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'confirmRevokeServiceAccountCredential',
        source: ServiceAccountsScreens.serviceAccountDetailsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86954',
    }),
    editServiceAccontDetails: (attributes: { orgId: string; serviceAccountId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'editServiceAccontDetails',
        source: ServiceAccountsScreens.serviceAccountDetailsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86955',
    }),
    confirmEditServiceAccontDetails: (attributes: { orgId: string; serviceAccountId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'confirmEditServiceAccontDetails',
        source: ServiceAccountsScreens.serviceAccountDetailsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86959',
    }),
  },
  track: {
    serviceAccountCreated: (attributes: { orgId: string }): TrackEvent => ({
      data: {
        action: TrackAction.Created,
        actionSubject: TrackActionSubject.ServiceAccount,
        actionSubjectId: 'serviceAccountCreated',
        source: ServiceAccountsScreens.serviceAccountListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87304',
    }),
    serviceAccountDeleted: (attributes: { orgId: string; serviceAccountId: string }): TrackEvent => ({
      data: {
        action: TrackAction.Deleted,
        actionSubject: TrackActionSubject.ServiceAccount,
        actionSubjectId: 'serviceAccountDeleted',
        source: ServiceAccountsScreens.serviceAccountListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87303',
    }),
    serviceAccountCredentialCreated: (attributes: { orgId: string }): TrackEvent => ({
      data: {
        action: TrackAction.Created,
        actionSubject: TrackActionSubject.ServiceAccountCredential,
        actionSubjectId: 'serviceAccountCredentialCreated',
        source: ServiceAccountsScreens.serviceAccountDetailsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87305',
    }),
    serviceAccountCredentialRevoked: (attributes: {
      orgId: string;
      serviceAccountId: string;
      credentialId: string | string[];
    }): TrackEvent => ({
      data: {
        action: TrackAction.Revoked,
        actionSubject: TrackActionSubject.ServiceAccountCredential,
        actionSubjectId: 'serviceAccountCredentialRevoked',
        source: ServiceAccountsScreens.serviceAccountDetailsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87306',
    }),
  },
  operational: {},
};
